import React, { useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useFileUpload } from '../../hooks/file/useFileUpload';

interface FileUploadProps {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ open, onClose, onUpload }) => {
  const theme = useTheme();
  const { uploadFile, isUploading, error } = useFileUpload();

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      try {
        for (const file of acceptedFiles) {
          await uploadFile(file);
        }
        onUpload();
        onClose();
      } catch (error) {}
      return false;
    },
    [uploadFile, onUpload, onClose]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
    },
    disabled: isUploading,
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6">Upload Files</Typography>
        <IconButton onClick={onClose} size="small">
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          {...getRootProps()}
          sx={{
            p: 6,
            border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: 1,
            backgroundColor: isDragActive ? `${theme.palette.primary.main}08` : 'transparent',
            cursor: isUploading ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease-in-out',
            textAlign: 'center',
          }}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography>Uploading files...</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  backgroundColor: `${theme.palette.primary.main}15`,
                  borderRadius: 1,
                  p: 1,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto',
                  mb: 2,
                }}
              >
                <Upload size={24} color={theme.palette.primary.main} />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                {isDragActive ? 'Drop files here' : 'Drag and drop files here'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                or click to select files
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Supported formats: PDF & TXT
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FileUpload;
